// Vehicle archetypes (overhaul Phase 3A).
//
// THE VARIANT POOL: eight bodies, each generated ONCE from a fixed key and cached; placements
// clone from the pool. Per-entity variation is paint tint (vertex colors -- one welded bucket,
// per-car colour), never new base meshes. rngFor(key) selects and configures; it does not mint
// geometry per entity.
//
// Construction: the body is an extrudeProfile of a real side silhouette -- bumper, hood, sloped
// windshield, roof falling to a rear deck -- with the wheel arches cut INTO the profile, not
// pasted on. Glass is a recessed second extrusion behind pillars. Tyres are lathe revolves with
// circumferential tread grooves. Lights are recessed liner housings with emissive lenses set back
// behind the aperture. The underbody is a real pan with exhaust runs, because a car with a flat
// void underneath reads as a toy from any low camera.
//
// Slots used: paint (vertex-tinted), glassCar, chrome, rubber, liner, trim, lens, lensRed.

import * as THREE from 'three';
import { beveledBox, extrudeProfile, loftBox, strut, place, placeParts } from './procedural.js';
import { rngFor } from '../rng.js';

const TAU = Math.PI * 2;

/** Lathe a tyre + rim + hub, axis along X, centred at origin. */
function wheel(R, W, opts = {}) {
  const seg = opts.seg ?? 18;
  const Ri = R * 0.60;                    // rim radius
  const parts = [];
  // Tyre profile in (radius, x) with three circumferential grooves. LatheGeometry revolves
  // points (x=radius, y=axis) around Y; we build along Y then rotate the axis onto X.
  const p = [];
  const w2 = W / 2;
  p.push([Ri, -w2 * 0.92]);
  p.push([R * 0.94, -w2]);
  const grooves = 3;
  for (let g = 0; g <= grooves; g++) {
    const t0 = -w2 + (W / (grooves + 1)) * g + W * 0.06;
    const t1 = -w2 + (W / (grooves + 1)) * (g + 1) - W * 0.06;
    p.push([R, t0]); p.push([R, t1]);
    if (g < grooves) { p.push([R * 0.965, t1 + W * 0.02]); p.push([R * 0.965, t1 + W * 0.05]); }
  }
  p.push([R * 0.94, w2]);
  p.push([Ri, w2 * 0.92]);
  const lathe = new THREE.LatheGeometry(p.map(([r, y]) => new THREE.Vector2(r, y)), seg);
  parts.push({ geo: place(lathe, { rot: [0, 0, Math.PI / 2] }), slot: 'rubber' });
  // Rim dish + spokes + hub.
  parts.push({ geo: place(new THREE.CylinderGeometry(Ri, Ri * 0.94, W * 0.5, seg), { rot: [0, 0, Math.PI / 2] }), slot: 'chrome' });
  for (let s = 0; s < 6; s++) {
    const a = (s / 6) * TAU;
    const spoke = beveledBox(W * 0.3, Ri * 0.8, Ri * 0.22, { bevel: 0.006, slot: 'chrome' });
    placeParts(spoke, { rot: [a, 0, 0] });
    placeParts(spoke, { pos: [W * 0.08, Math.sin(a + Math.PI / 2) * Ri * 0.4 * 0, 0] });
    parts.push(...spoke);
  }
  parts.push({ geo: place(new THREE.CylinderGeometry(Ri * 0.22, Ri * 0.22, W * 0.62, 10), { rot: [0, 0, Math.PI / 2] }), slot: 'trim' });
  return parts;
}

/**
 * One archetype. `kind` 0..7: 0-4 saloon/hatch mixes, 5 wagon, 6-7 van. Dimensions are the
 * CALLER's (they own collision and parking spacing); the key only shapes proportions inside them.
 */
export function vehicleArchetype(idx, L, W, H) {
  const r = rngFor(`veh.arch.${idx}`);
  const van = idx >= 6;
  const parts = [];

  const clearance = 0.16;
  const axleY = 0.30;
  const archR = van ? 0.40 : r.range(0.37, 0.42);
  const zF = L * 0.32, zR = -L * 0.33;          // axle positions
  const beltY = van ? H * 0.52 : H * r.range(0.46, 0.52);       // top of the body side
  const roofY = H;
  const hoodY = van ? H * 0.9 : H * r.range(0.60, 0.68);
  const noseZ = L / 2, tailZ = -L / 2;
  const wsBase = van ? L * 0.30 : L * r.range(0.10, 0.16);       // windshield base z
  const roofF = van ? L * 0.24 : L * r.range(0.02, 0.07);        // roof front edge
  const roofR = van ? -L * 0.44 : -L * (0.30 + (idx === 5 ? 0.12 : r.range(0, 0.06)));
  const deckY = van ? H * 0.9 : beltY + (idx % 3 === 0 ? 0.06 : 0.02);

  // --- body silhouette in (z, y), arches cut into the bottom edge ----------
  const s = new THREE.Shape();
  s.moveTo(tailZ + 0.1, clearance);
  // rear arch
  s.lineTo(zR + archR + 0.05, clearance);
  s.absarc(zR, axleY - 0.06, archR, Math.PI + 0.35, -0.35, true);
  // between arches
  s.lineTo(zF - archR - 0.05, clearance);
  s.absarc(zF, axleY - 0.06, archR, Math.PI + 0.35, -0.35, true);
  s.lineTo(noseZ - 0.12, clearance);
  // nose
  s.lineTo(noseZ, clearance + 0.18);
  s.lineTo(noseZ - 0.02, beltY * 0.8);
  s.lineTo(noseZ - 0.06, hoodY);
  // hood -> windshield -> roof
  s.lineTo(wsBase, van ? hoodY : hoodY + 0.01);
  s.lineTo(roofF, roofY);
  s.lineTo(roofR, roofY);
  // rear screen -> deck -> tail
  s.lineTo(roofR - (van ? 0.02 : L * 0.10), deckY);
  s.lineTo(tailZ + 0.06, deckY);
  s.lineTo(tailZ, beltY * 0.72);
  s.lineTo(tailZ + 0.1, clearance);
  s.closePath();

  const bodyW = W * 0.94;
  const body = extrudeProfile(s, { depth: bodyW, bevel: 0.035, bevelSegments: 2, curveSegments: 5, slot: 'paint' });
  placeParts(body, { rot: [0, Math.PI / 2, 0] });      // extrusion axis -> X (width)
  parts.push(...body);

  // --- greenhouse glass: recessed band under the roofline -------------------
  const g = new THREE.Shape();
  const gB = beltY + 0.03, gT = roofY - 0.045;
  g.moveTo(wsBase + 0.05, gB);
  g.lineTo(roofF + 0.06, gT);
  g.lineTo(roofR - 0.04, gT);
  g.lineTo(roofR - (van ? 0.0 : L * 0.085), gB + 0.01);
  g.closePath();
  const glass = extrudeProfile(g, { depth: bodyW - 0.10, bevel: 0.012, slot: 'glassCar' });
  placeParts(glass, { rot: [0, Math.PI / 2, 0] });
  parts.push(...glass);
  // Pillar band (trim) framing the glass, slightly narrower than the body so the glass reads
  // recessed 0.05 with a border seal.
  const seal = extrudeProfile(g, { depth: bodyW - 0.04, bevel: 0.008, slot: 'rubber' });
  placeParts(seal, { scale: [1, 1.02, 1], rot: [0, Math.PI / 2, 0] });
  parts.push(...seal.map((p) => ({ ...p })));

  // --- wheels ---------------------------------------------------------------
  for (const [z, side] of [[zF, -1], [zF, 1], [zR, -1], [zR, 1]]) {
    const wparts = wheel(archR * 0.92, 0.24);
    placeParts(wparts, { pos: [side * (W / 2 - 0.10), axleY, z] });
    parts.push(...wparts);
  }

  // --- face: grille, lights, bumpers ---------------------------------------
  const grille = beveledBox(W * 0.44, 0.16, 0.05, {
    bevel: 0.01, slot: 'liner', panels: { faces: ['pz'], nx: 6, ny: 2, gap: 0.012, depth: 0.012 },
  });
  placeParts(grille, { pos: [0, clearance + 0.24, noseZ - 0.005] });
  parts.push(...grille);
  for (const side of [-1, 1]) {
    // Headlight: liner housing recessed, lens set BACK behind the aperture.
    const housing = beveledBox(0.26, 0.12, 0.09, { bevel: 0.012, slot: 'liner' });
    placeParts(housing, { pos: [side * W * 0.30, beltY * 0.62, noseZ - 0.028] });
    parts.push(...housing);
    parts.push({
      // 2 mm PROUD of the housing front: the first placement put the lens INSIDE the housing
      // solid, where an emissive plane lights nothing. Recessed reads from the liner frame
      // standing around it, not from burying the emitter.
      geo: place(new THREE.PlaneGeometry(0.20, 0.08), { pos: [side * W * 0.30, beltY * 0.62, noseZ + 0.021] }),
      slot: 'lens',
    });
    const tail = beveledBox(0.20, 0.10, 0.07, { bevel: 0.01, slot: 'liner' });
    placeParts(tail, { pos: [side * W * 0.30, beltY * 0.66, tailZ + 0.02] });
    parts.push(...tail);
    parts.push({
      geo: place(new THREE.PlaneGeometry(0.15, 0.06), { rot: [0, Math.PI, 0], pos: [side * W * 0.30, beltY * 0.66, tailZ - 0.019] }),
      slot: 'lensRed',
    });
    // Mirrors.
    const mir = beveledBox(0.05, 0.08, 0.14, { bevel: 0.008, slot: 'trim' });
    placeParts(mir, { pos: [side * (W / 2 + 0.05), beltY + 0.10, wsBase + 0.10] });
    parts.push(...mir);
  }
  // Bumpers: lofted bars wider than the body, chamfered.
  for (const [z, sign] of [[noseZ + 0.02, 1], [tailZ - 0.02, -1]]) {
    const bump = loftBox([[-W * 0.46, 0.055, 0.05], [0, 0.062, 0.075, 0, sign * 0.012], [W * 0.46, 0.055, 0.05]], 0.02, { slot: 'trim' });
    placeParts(bump, { rot: [0, 0, Math.PI / 2], pos: [0, clearance + 0.10, z] });
    parts.push(...bump);
  }

  // --- underbody: pan, exhaust, axles --------------------------------------
  const pan = beveledBox(W * 0.82, 0.05, L * 0.62, { bevel: 0.012, slot: 'liner' });
  placeParts(pan, { pos: [0, clearance - 0.02, (zF + zR) / 2] });
  parts.push(...pan);
  for (const z of [zF, zR]) {
    parts.push(...strut([-W * 0.36, axleY, z], [W * 0.36, axleY, z], 0.035, 0.035, { slot: 'trim', seg: 8 }));
  }
  parts.push(...strut([W * 0.22, clearance - 0.01, zR + 0.1], [W * 0.28, clearance + 0.02, tailZ + 0.05], 0.028, 0.028, { slot: 'trim', seg: 7 }));

  // Roof accessory on some archetypes: rails or a beacon bar -- bolt-on pool variety.
  if (r.chance(0.5) && !van) {
    for (const side of [-1, 1]) {
      parts.push(...strut([side * W * 0.32, roofY + 0.03, roofF - 0.05], [side * W * 0.32, roofY + 0.03, roofR + 0.05], 0.018, 0.018, { slot: 'trim', seg: 6 }));
    }
  }
  if (van) {
    const rack = beveledBox(W * 0.7, 0.05, L * 0.5, { bevel: 0.01, slot: 'trim' });
    placeParts(rack, { pos: [0, roofY + 0.05, (roofF + roofR) / 2] });
    parts.push(...rack);
  }
  return parts;
}

// ---------------------------------------------------------------------------
// The pool + placement
// ---------------------------------------------------------------------------
const CACHE = new Map();

/** Archetype from the pool, built once. Returns CLONES ready to place -- callers own them. */
export function vehicleFromPool(idx, L, W, H) {
  const key = `${idx}|${L.toFixed(2)}|${W.toFixed(2)}|${H.toFixed(2)}`;
  if (!CACHE.has(key)) CACHE.set(key, vehicleArchetype(idx, L, W, H));
  return CACHE.get(key).map((p) => ({ geo: p.geo.clone(), slot: p.slot }));
}

export const PAINTS = [0x2c3540, 0x4a3026, 0x24382e, 0x3a3a42, 0x552e2e, 0x2e4050, 0x3d3d35, 0x1e2226];

/** Bake a per-vehicle paint tint as vertex colors onto the paint-slot parts. */
export function tintPaint(parts, hex) {
  const c = new THREE.Color(hex);
  for (const p of parts) {
    if (p.slot !== 'paint') continue;
    const n = p.geo.attributes.position.count;
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
    p.geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  }
  return parts;
}
